import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { ContractStateProps, ExtensionCategory, ExtensionType } from '../../types'
import { MobileDrawer } from 'src/Elements/Drawer'
import { DialogTitle, IconButton } from '@mui/material'
import { Box } from '@mui/system'
import CloseIcon from '@mui/icons-material/Close'
import Description from './Description'
import { Chip } from 'src/Components'
import ContractState from './ContractState'

interface StyleProps {
  extensionType: ExtensionType
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  drawer: {
    '&  .MuiDrawer-paper': {
      padding: theme.spacing(4),
      paddingTop: 0,
      gap: theme.spacing(4),
    },
  },
  drawerBar: {
    backgroundColor: theme.palette.grey.grey03,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: 0,
  },
  closeIcon: {
    color: theme.palette.grey.grey00,
  },
  body: {
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    flex: 1,
    gap: theme.spacing(4),
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'hidden',
  },
  description: {
    minHeight: 80,
    overflowY: 'auto',
    scrollbarWidth: 'thin' /** Firefox */,
    '&::-webkit-scrollbar': {
      position: 'absolute',
      width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey.grey06,
      height: '80%',
      borderRadius: 12,
    },
  },
}))

interface Props {
  extensionType: ExtensionType
  descriptions: string[]
  headerContent: React.ReactElement
  bodyContent?: React.ReactElement
  footerContent: React.ReactElement
  onClose: () => void
  open: boolean
  category: ExtensionCategory
  contractStateProps?: ContractStateProps
  linkHref?: string
  onLinkClick?: () => void
}

/**
 * A drawer component used for mobile screens to display detailed extension information.
 */
const DetailsDrawer: React.FC<Props> = ({
  extensionType,
  descriptions,
  headerContent,
  bodyContent,
  footerContent,
  onClose,
  open,
  category,
  contractStateProps,
  linkHref,
  onLinkClick,
}) => {
  const { classes } = useStyles({ extensionType })
  const categoryName = `installation-extension.category.${category?.name}`

  return (
    <MobileDrawer onClose={onClose} open={open} containerStyle={classes.drawer} barStyle={classes.drawerBar}>
      <DialogTitle className={classes.header}>
        <Box display="flex" justifyContent="space-between">
          {headerContent}
          <IconButton className={classes.closeIcon} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Chip textKey={categoryName} paletteColor={category.paletteColor} isLoading={false} variant={category.variant} />
        </Box>
      </DialogTitle>
      <Box className={classes.body}>
        {descriptions.length ? (
          <Description textParts={descriptions} style={classes.description} linkHref={linkHref} onLinkClick={onLinkClick} />
        ) : null}
        {bodyContent}
        {footerContent}
        <ContractState {...contractStateProps} sx={{ marginTop: 2 }} />
      </Box>
    </MobileDrawer>
  )
}

export default DetailsDrawer
